import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { CheckCircle, FileText, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DISCLAIMER = `This term sheet is provided for informational and discussion purposes only and does not constitute a commitment to lend, extend credit, or enter into any binding agreement. All terms and conditions outlined herein are subject to change without notice and are contingent upon, but not limited to, full underwriting review, verification of information, satisfactory appraisal, title review, and final credit approval.

This document does not represent a loan approval, guarantee of funding, or final loan terms. Final terms will be set forth in definitive loan documents, which shall govern in all respects.`;

const SignTermSheet = () => {
  const { termSheetId } = useParams();
  const [termSheet, setTermSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [printedName, setPrintedName] = useState('');
  const [signatureDate, setSignatureDate] = useState(new Date().toISOString().split('T')[0]);
  const sigCanvas = useRef(null);

  useEffect(() => {
    fetchTermSheet();
  }, [termSheetId]);

  const fetchTermSheet = async () => {
    try {
      const response = await axios.get(`${API}/term-sheets/${termSheetId}/public`);
      setTermSheet(response.data);
      if (response.data.status === 'signed') {
        setSigned(true);
      }
    } catch (error) {
      toast.error('Failed to load term sheet');
    } finally {
      setLoading(false);
    }
  };

  const clearSignature = () => {
    sigCanvas.current?.clear();
  };

  const handleSign = async () => {
    if (!printedName.trim()) {
      toast.error('Please enter your printed name');
      return;
    }

    if (sigCanvas.current?.isEmpty()) {
      toast.error('Please provide your signature');
      return;
    }

    setSigning(true);
    try {
      const signatureData = sigCanvas.current.toDataURL('image/png');
      
      await axios.post(`${API}/term-sheets/${termSheetId}/sign`, {
        printed_name: printedName,
        signature_data: signatureData,
        signed_date: signatureDate
      });

      toast.success('Term sheet signed successfully!');
      setSigned(true);
    } catch (error) {
      toast.error('Failed to sign term sheet');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!termSheet) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Term Sheet Not Found</h2>
            <p className="text-muted-foreground">This term sheet may have expired or been removed.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Term Sheet Signed</h2>
            <p className="text-muted-foreground mb-4">
              Thank you for signing the term sheet. A copy has been sent to your email.
            </p>
            {termSheet.signed_at && (
              <p className="text-sm text-muted-foreground">
                Signed on: {new Date(termSheet.signed_at).toLocaleString()}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_loan-tracker-75/artifacts/5da0ujdt_axe%20logo%20corregido-01.png" 
            alt="Axe Capital Lending" 
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-gray-900">Loan Term Sheet</h1>
          <p className="text-muted-foreground mt-2">Please review and sign below</p>
        </div>

        {/* Term Sheet Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Term Sheet Details
            </CardTitle>
            <CardDescription>
              {termSheet.terms.program_name} - {termSheet.terms.lender_name}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Borrower Info */}
            <div>
              <h3 className="font-semibold text-sm text-muted-foreground mb-3">BORROWER INFORMATION</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground text-xs">Borrower Name</Label>
                  <p className="font-medium">{termSheet.borrower_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Property Address</Label>
                  <p className="font-medium">{termSheet.property_address}</p>
                </div>
              </div>
            </div>

            <Separator />

            {/* Loan Details */}
            <div>
              <h3 className="font-semibold text-sm text-muted-foreground mb-3">LOAN TERMS</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <Label className="text-muted-foreground text-xs">Loan Amount</Label>
                  <p className="font-bold text-xl text-primary">${termSheet.loan_amount?.toLocaleString()}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <Label className="text-muted-foreground text-xs">Interest Rate</Label>
                  <p className="font-bold text-xl">{termSheet.interest_rate}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <Label className="text-muted-foreground text-xs">Term</Label>
                  <p className="font-bold text-xl">{termSheet.term_months} months</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <Label className="text-muted-foreground text-xs">LTV</Label>
                  <p className="font-bold text-xl">{termSheet.ltv}%</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <Label className="text-muted-foreground text-xs">Loan Type</Label>
                  <p className="font-bold">{termSheet.terms.loan_type}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <Label className="text-muted-foreground text-xs">Lender</Label>
                  <p className="font-bold">{termSheet.terms.lender_name}</p>
                </div>
              </div>
            </div>

            {termSheet.terms.notes && (
              <>
                <Separator />
                <div>
                  <h3 className="font-semibold text-sm text-muted-foreground mb-2">ADDITIONAL NOTES</h3>
                  <p className="text-sm">{termSheet.terms.notes}</p>
                </div>
              </>
            )}

            <Separator />

            {/* Disclaimer */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="font-semibold text-sm text-yellow-800 mb-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                IMPORTANT DISCLAIMER
              </h3>
              <p className="text-xs text-yellow-700 whitespace-pre-line">{DISCLAIMER}</p>
            </div>
          </CardContent>
        </Card>

        {/* Signature Section */}
        <Card>
          <CardHeader>
            <CardTitle>Sign Term Sheet</CardTitle>
            <CardDescription>
              By signing below, you acknowledge that you have read and understood the terms above.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Printed Name */}
            <div className="space-y-2">
              <Label htmlFor="printedName">Printed Name *</Label>
              <Input
                id="printedName"
                value={printedName}
                onChange={(e) => setPrintedName(e.target.value)}
                placeholder="Enter your full legal name"
                className="max-w-md"
              />
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label htmlFor="signDate">Date *</Label>
              <Input
                id="signDate"
                type="date"
                value={signatureDate}
                onChange={(e) => setSignatureDate(e.target.value)}
                className="max-w-md"
              />
            </div>

            {/* Signature Pad */}
            <div className="space-y-2">
              <Label>Signature *</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 bg-white">
                <SignatureCanvas
                  ref={sigCanvas}
                  canvasProps={{
                    width: 500,
                    height: 150,
                    className: 'signature-canvas w-full',
                    style: { maxWidth: '100%', touchAction: 'none' }
                  }}
                  backgroundColor="white"
                />
              </div>
              <p className="text-xs text-muted-foreground">Sign above using your mouse or finger</p>
              <Button variant="outline" size="sm" onClick={clearSignature}>
                Clear Signature
              </Button>
            </div>

            {/* Submit */}
            <div className="pt-4">
              <Button 
                onClick={handleSign} 
                disabled={signing}
                size="lg"
                className="w-full md:w-auto"
              >
                {signing ? 'Signing...' : 'Sign & Submit Term Sheet'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-xs text-muted-foreground py-4">
          <p>Axe Capital Lending</p>
          <p>This is a secure document signing page.</p>
        </div>
      </div>
    </div>
  );
};

export default SignTermSheet;
